import { DatalayerService } from '@analytics/datalayer';
import {
  AnalyticsActionFieldTypes,
  AnalyticsActionTypes,
  ToolTrackValues,
} from '@analytics/models';
import { processAnalyticsValue, ToolTrackingUtils } from '@analytics/utils';
import { Directive, HostListener, inject, Input } from '@angular/core';
import type { RichTextField, TextField } from '@core/jss-proxy';
import { sanitizeFieldValue } from '@core/jss-utils';

/**
 * Configuration interface for field value tracking.
 */
interface TrackFieldValueModel extends ToolTrackValues {
  /** Description of the field being tracked */
  label: TextField | RichTextField | string;

  /** Whether to track focus events */
  trackFocus: boolean;

  /** Whether to include the field value in analytics */
  sendValue: boolean;

  /** Whether to hash the field value before sending */
  hashValue?: boolean;

  /** Description of the selected value */
  inputLabel: TextField | RichTextField | string;

  /** Type of analytics action for the field */
  type?: AnalyticsActionFieldTypes;
}

/**
 * Angular directive for tracking value changes in form components.
 * Designed for components like radio buttons, checkboxes, and select dropdowns.
 *
 * WARNING: Do NOT use this directive on text input fields as it will trigger
 * analytics events for every keystroke.
 *
 * Usage:
 * ```html
 * <select [wlTrackValueChange]="{
 *   label: 'preference',
 *   inputLabel: 'selectedOption',
 *   sendValue: true,
 *   trackFocus: true,
 *   step: 1
 * }">
 * ```
 */
@Directive({
  selector: '[wlTrackValueChange]',
  standalone: true,
})
export class TrackValueChangeDirective {
  readonly #datalayer = inject(DatalayerService);
  readonly #toolTrackingUtils = inject(ToolTrackingUtils);

  // Track state of field interactions
  #hasFired?: 'focus' | 'change';
  #previousValue?: string;

  /**
   * Default configuration values
   */
  readonly #defaultValues: TrackFieldValueModel = {
    label: '',
    trackFocus: false,
    sendValue: false,
    hashValue: false,
    inputLabel: '',
    step: 0,
    type: AnalyticsActionTypes.FIELD_FOCUS,
  };

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackValueChange') trackValueChange: TrackFieldValueModel =
    this.#defaultValues;

  /**
   * Gets the merged configuration values (defaults + user provided)
   * @private
   * @returns {TrackFieldValueModel}
   */
  getValues = () => ({
    ...this.#defaultValues,
    ...this.trackValueChange,
  });

  /**
   * Handles the field focus event
   * Only fires if focus tracking is enabled and hasn't fired before
   */
  @HostListener('focus')
  async onFocus() {
    const values = this.getValues();

    // Skip if we've already fired a focus event, or focus tracking is disabled
    if (this.#hasFired || !values.trackFocus) {
      return;
    }

    const fieldname = sanitizeFieldValue(values.label);

    // Create and send completion analytics event
    const event = this.#toolTrackingUtils.createToolEvent(
      AnalyticsActionTypes.FIELD_FOCUS,
      {
        fieldname,
        result: {
          [fieldname]: processAnalyticsValue(undefined, values),
        },
      },
      await this.#toolTrackingUtils.getToolMetadata(values)
    );

    this.#datalayer.pushEvent(event);

    // Set a focus flag if we haven't fired yet.
    if (!this.#hasFired) {
      this.#hasFired = 'focus';
    }
  }

  /**
   * Handles the field change event.
   * Tracks value changes based on configuration.
   */
  @HostListener('change') async onChange() {
    // Run in ngZone to ensure the viewmodel is in sync with the input value.
    const values = await new Promise<TrackFieldValueModel>((resolve) =>
      setTimeout(() => resolve(this.getValues()), 1)
    );

    // Skip if we've already fired a change event, or we do not want to send the value
    if (this.#hasFired === 'change' && !values.sendValue) {
      return;
    }

    const value = sanitizeFieldValue(values.inputLabel);

    // Skip if the value has not changed.
    if (this.#previousValue === value) {
      return;
    }

    const fieldname = sanitizeFieldValue(values.label);

    // Create and send completion analytics event
    const event = this.#toolTrackingUtils.createToolEvent(
      AnalyticsActionTypes.FIELD_COMPLETE,
      {
        fieldname,
        result: {
          [fieldname]: processAnalyticsValue(value, values),
        },
      },
      await this.#toolTrackingUtils.getToolMetadata(values)
    );

    this.#datalayer.pushEvent(event);
    this.#hasFired = 'change';
    this.#previousValue = value;
  }
}
